import { useMutation, useQuery } from '@apollo/client';
import { Label } from 'components/Label/Label';
import { Select } from 'components/Select/Select';
import { useToast } from 'components/Toast/use-toast';
import { extractCaughtError, extractGraphQLError } from 'lib/graphql-error-handler';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MEMBER_TAGS_UPDATE } from 'services/tags/mutation';
import { MEMBER_TAGS } from 'services/tags/query';

type TagOption = { readonly value: string; readonly label: string };

type MemberTagsFieldProps = {
    memberId: string;
    initialTagIds: string[];
};

type MemberTagNode = { readonly id: string; readonly name: string };

export const MemberTagsField = ({ memberId, initialTagIds }: MemberTagsFieldProps) => {
    const { t } = useTranslation('member');
    const { t: f } = useTranslation('forms');
    const { toast } = useToast();
    const tags = useQuery<{ memberTags: MemberTagNode[] }>(MEMBER_TAGS);
    const [updateTags, { loading }] = useMutation(MEMBER_TAGS_UPDATE);
    const [selectedIds, setSelectedIds] = useState<string[]>(initialTagIds);

    const initialKey = initialTagIds.join(',');
    useEffect(() => {
        setSelectedIds(initialKey ? initialKey.split(',') : []);
    }, [initialKey]);

    const options: TagOption[] = (tags.data?.memberTags ?? []).map(tag => ({ value: tag.id, label: tag.name }));
    const value = options.filter(o => selectedIds.includes(o.value));

    const showError = (message: string) =>
        toast({ title: t('updateErrorTitle'), description: message, variant: 'warning' });

    const onChange = async (selected: readonly TagOption[]) => {
        const previous = selectedIds;
        const next = selected.map(o => o.value);
        setSelectedIds(next);
        try {
            const resp = await updateTags({ variables: { tagIds: next, member: memberId } });
            if (resp.errors) {
                setSelectedIds(previous);
                showError(extractGraphQLError(resp.errors, t('updateErrorMessage')));
                return;
            }
            toast({ title: t('tagsSaved'), variant: 'success' });
        } catch (e) {
            setSelectedIds(previous);
            showError(extractCaughtError(e, t('updateErrorMessage')));
        }
    };

    return (
        <div>
            <Label>{t('tagsLabel')}</Label>
            <Select<TagOption, true>
                isMulti
                isLoading={tags.loading || loading}
                isDisabled={loading}
                placeholder={f('selectPlaceholder')}
                options={options}
                value={value}
                onChange={onChange}
            />
        </div>
    );
};
